import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Video, Eye, Clock, TrendingUp, ArrowRight } from 'lucide-react'
import { useAnalyticsStore } from '../stores/analyticsStore'
import { ViewsLineChart, StatsPieChart, ChartLegend } from '../components/AnalyticsChart'
import { StatCard, Spinner, EmptyState } from '../components/common'
import { formatDuration, formatNumber } from '../hooks'

export default function Dashboard() {
  const { dashboard, isLoading, error, fetchDashboard, periodDays, setPeriodDays } =
    useAnalyticsStore()

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  if (isLoading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        title="Failed to load dashboard"
        description={error}
        action={
          <button onClick={() => fetchDashboard()} className="btn-primary">
            Try Again
          </button>
        }
      />
    )
  }

  if (!dashboard) {
    return (
      <EmptyState
        icon={<Video className="w-12 h-12" />}
        title="No data yet"
        description="Upload your first video to start tracking views"
        action={
          <Link to="/videos" className="btn-primary">
            Upload Video
          </Link>
        }
      />
    )
  }

  const { summary, views_by_day, top_videos, geo_breakdown, device_breakdown } = dashboard

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Overview of your video performance</p>
        </div>

        <select
          value={periodDays}
          onChange={(e) => {
            setPeriodDays(Number(e.target.value))
            fetchDashboard(Number(e.target.value))
          }}
          className="select w-48"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Videos"
          value={summary.total_videos}
          icon={<Video className="w-5 h-5" />}
        />
        <StatCard
          label="Total Views"
          value={formatNumber(summary.total_views)}
          icon={<Eye className="w-5 h-5" />}
        />
        <StatCard
          label="Watch Time"
          value={formatDuration(summary.total_watch_time)}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          label="Avg. Completion"
          value={`${Math.round(summary.avg_completion)}%`}
          icon={<TrendingUp className="w-5 h-5" />}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Views over time */}
        <div className="lg:col-span-2 card">
          <h3 className="text-lg font-semibold text-white mb-4">Views Over Time</h3>
          {views_by_day.length > 0 ? (
            <ViewsLineChart data={views_by_day} dataKey="views" xDataKey="date" />
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">
              No view data for this period
            </div>
          )}
        </div>

        {/* Device breakdown */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Devices</h3>
          {device_breakdown.length > 0 ? (
            <>
              <StatsPieChart
                data={device_breakdown.map((d) => ({
                  name: d.device_type,
                  value: d.views,
                }))}
                dataKey="value"
                height={200}
              />
              <ChartLegend
                items={device_breakdown.map((d) => ({
                  name: d.device_type,
                  value: d.views,
                }))}
              />
            </>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400">
              No device data
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top videos */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Top Videos</h3>
            <Link
              to="/videos"
              className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {top_videos.length > 0 ? (
            <div className="space-y-3">
              {top_videos.map((video, index) => (
                <Link
                  key={video.id}
                  to={`/videos/${video.id}`}
                  className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-sm font-medium text-slate-300">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{video.title}</p>
                    <p className="text-sm text-slate-400">
                      {formatNumber(video.views)} views • {formatDuration(video.watch_time)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400">
              No videos with views yet
            </div>
          )}
        </div>

        {/* Geographic breakdown */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Top Locations</h3>

          {geo_breakdown.length > 0 ? (
            <div className="space-y-3">
              {geo_breakdown.slice(0, 5).map((geo) => (
                <div
                  key={geo.country}
                  className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {/* Flag emoji based on country code */}
                      {geo.country
                        ? String.fromCodePoint(
                            ...geo.country
                              .toUpperCase()
                              .split('')
                              .map((char) => 127397 + char.charCodeAt(0))
                          )
                        : '🌍'}
                    </span>
                    <span className="text-white">{geo.country_name || 'Unknown'}</span>
                  </div>
                  <span className="text-slate-400">{formatNumber(geo.views)} views</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400">
              No location data available
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
